import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2,
  CheckCircle,
  PenTool,
  Clock,
  Mail,
  ShieldCheck,
  MessageSquare,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const SUBJECTS = [
  'General enquiry',
  'Support',
  'Multi-Academy Trust pricing',
  'Privacy / Data request',
  'Safeguarding concern',
  'Billing',
] as const;

declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad: () => void;
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string ?? '';

export default function Contact() {
  const { toast } = useToast();
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (document.getElementById('cf-turnstile-script')) return;
    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    function renderWidget() {
      if (!captchaRef.current || !window.turnstile || !TURNSTILE_SITE_KEY) return;
      if (widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(captchaRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
        theme: 'light',
      });
    }

    window.onTurnstileLoad = renderWidget;
    if (window.turnstile) renderWidget();

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!turnstileToken) {
      toast({ title: 'Please complete the CAPTCHA', variant: 'destructive' });
      return;
    }
    if (!subject) {
      toast({ title: 'Please select a subject', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('contact', {
        body: { name, email, subject, message, turnstileToken },
      });

      if (error || !data?.success) {
        const msg = data?.error ?? error?.message ?? 'Something went wrong. Please try again.';
        toast({ title: 'Failed to send', description: msg, variant: 'destructive' });
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          setTurnstileToken('');
        }
      } else {
        setSent(true);
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title="Contact Us"
        description="Get in touch with the WriteAssess team. We're here to help with questions about our platform, billing, or data protection."
        canonical="/contact"
      />
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <PenTool className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl">WriteAssess</span>
          </Link>
          <Link
            to="/auth"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        {/* Page hero */}
        <div className="border-b bg-muted/30">
          <div className="max-w-6xl mx-auto px-6 py-12">
            <h1 className="text-4xl font-bold text-foreground tracking-tight">Contact Us</h1>
            <p className="mt-3 text-lg text-muted-foreground max-w-xl">
              We're here to help. Send us a message and we'll get back to you promptly.
            </p>
          </div>
        </div>

        {sent ? (
          <div className="max-w-6xl mx-auto px-6 py-24 flex flex-col items-center text-center gap-5">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold">Message sent</h2>
            <p className="text-muted-foreground max-w-sm">
              Thanks for getting in touch. We aim to respond within one business day.
            </p>
            <Button variant="outline" asChild className="mt-2">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-6 py-12 lg:py-16">
            <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">

              {/* Left: info panel */}
              <div className="lg:col-span-2 space-y-8">
                <div className="space-y-6">
                  <InfoItem
                    icon={<Clock className="h-5 w-5 text-primary" />}
                    title="Response time"
                    description="We aim to respond to all enquiries within one business day."
                  />
                  <InfoItem
                    icon={<Mail className="h-5 w-5 text-primary" />}
                    title="Email support"
                    description="You'll receive a reply directly to the email address you provide."
                  />
                  <InfoItem
                    icon={<ShieldCheck className="h-5 w-5 text-primary" />}
                    title="Safeguarding concerns"
                    description="For urgent safeguarding matters, also contact your school's Designated Safeguarding Lead directly."
                  />
                  <InfoItem
                    icon={<MessageSquare className="h-5 w-5 text-primary" />}
                    title="What to include"
                    description="The more detail you provide, the faster we can help. For support issues, include the relevant pupil year group or class if applicable."
                  />
                </div>
              </div>

              {/* Right: form */}
              <div className="lg:col-span-3">
                <div className="bg-card border rounded-xl shadow-sm p-5 sm:p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-1.5">
                        <Label htmlFor="name">
                          Full name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          maxLength={100}
                          required
                          disabled={submitting}
                          placeholder="Your full name"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="email">
                          Email address <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          maxLength={254}
                          required
                          disabled={submitting}
                          placeholder="you@school.co.uk"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="subject">
                        Subject <span className="text-destructive">*</span>
                      </Label>
                      <Select value={subject} onValueChange={setSubject} disabled={submitting}>
                        <SelectTrigger id="subject">
                          <SelectValue placeholder="Select a subject…" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBJECTS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="message">
                        Message <span className="text-destructive">*</span>
                        <span className="text-muted-foreground font-normal ml-2 text-xs">
                          ({message.length}/4000)
                        </span>
                      </Label>
                      <Textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={4000}
                        required
                        disabled={submitting}
                        placeholder="How can we help?"
                        rows={6}
                      />
                    </div>

                    <div>
                      <div ref={captchaRef} />
                      {!TURNSTILE_SITE_KEY && (
                        <p className="text-xs text-muted-foreground mt-1">
                          (CAPTCHA not configured — set VITE_TURNSTILE_SITE_KEY)
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitting || (!turnstileToken && !!TURNSTILE_SITE_KEY)}
                      className="w-full"
                    >
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {submitting ? 'Sending…' : 'Send message'}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      Fields marked <span className="text-destructive">*</span> are required.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t mt-auto">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground">
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/data-protection" className="hover:text-foreground transition-colors">Data Protection</Link>
            <Link to="/cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link>
            <Link to="/safeguarding" className="hover:text-foreground transition-colors">Safeguarding</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            <Link to="/help" className="hover:text-foreground transition-colors">Help</Link>
          </div>
          <p>© {new Date().getFullYear()} WriteAssess. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function InfoItem({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4">
      <div className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="font-medium text-foreground text-sm">{title}</p>
        <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
